import { FilePdfOutlined } from '@ant-design/icons'
import { Button } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import type React from 'react'
import { Component } from 'react'
import { useTranslation } from 'react-i18next'
import { gaEvent } from '../../common/services/analytics'
import { getJSON } from '../../common/services/api'
import { downloadPdfData, showToast } from '../../common/utils'

interface PdfExportProps {
  getDocDefinition?: (result: any) => Promise<any>
  hideIcon?: boolean
  pdfTitle: string
  style?: React.CSSProperties
  text?: string
  dataUrl?: string
  url?: string
  disabled?: boolean
}

interface PdfExportState {
  isLoading: boolean
}

type PdfExportBaseProps = PdfExportProps & {
  t: (key: string) => string
}

// PDF Fonts. These name entries in pdfmake's virtual file system rather than
// remote URLs: pdfmake used to fetch the Roboto files straight from
// cdnjs.cloudflare.com at generation time, so any user whose browser couldn't
// reach that CDN (content blocker, firewall, CDN outage) got no PDF at all. The
// base64 copies in ./pdfMakeFonts are bundled with the app instead.
const pdfFonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf'
  }
}

// pdfmake's `download` is asynchronous and has no error channel: it re-throws
// internal failures from inside a promise handler, so they surface as an
// unhandled rejection and the completion callback simply never fires. Waiting
// on it unbounded is what left the button spinning forever, so give up after a
// while and tell the user instead. Generous enough for a long care plan.
const DOWNLOAD_TIMEOUT_MS = 60000

class PdfExportBase extends Component<PdfExportBaseProps, PdfExportState> {
  state: PdfExportState = {
    isLoading: false
  }

  // Every failure path has to come through here: without it the button keeps
  // spinning forever and the user is given no clue that anything went wrong.
  handleError = (err: unknown): void => {
    console.error('PDF download error: ', err)
    this.setState({ isLoading: false })
    showToast({
      type: 'error',
      title: this.props.t(generalConstants.COULDNT_DOWNLOAD_PDF)
    })
  }

  createPdf = async (result: unknown): Promise<void> => {
    try {
      const { getDocDefinition, pdfTitle } = this.props
      // The fonts are ~700kB of base64, so they are loaded lazily alongside
      // pdfmake itself rather than being pulled into the main bundle.
      const [pdfMake, vfs] = await Promise.all([
        import('pdfmake/build/pdfmake').then((module) => module.default),
        import('./pdfMakeFonts').then((module) => module.default)
      ])
      const docDefinition = await getDocDefinition?.(result)

      let timeout: ReturnType<typeof setTimeout> | undefined
      try {
        await new Promise<void>((resolve, reject) => {
          timeout = setTimeout(() => {
            reject(new Error('Timed out generating the PDF'))
          }, DOWNLOAD_TIMEOUT_MS)
          pdfMake
            // @ts-ignore
            .createPdf(docDefinition, null, pdfFonts, vfs)
            .download(pdfTitle, resolve)
        })
      } finally {
        clearTimeout(timeout)
      }

      this.setState({ isLoading: false })
    } catch (err) {
      this.handleError(err)
    }
  }

  fetchPdf = (): void => {
    const { pdfTitle, url } = this.props
    getJSON({ url: url as string, responseType: 'arrayBuffer' })
      .then((res) => {
        downloadPdfData(res, pdfTitle)
        this.setState({ isLoading: false })
      })
      .catch(this.handleError)
  }

  fetchData = (url: string): void => {
    getJSON({ url })
      .then((res) => this.createPdf(res.result))
      .catch(this.handleError)
  }

  getPdf = (): void => {
    this.setState({ isLoading: true })
    if (this.props.url) {
      this.fetchPdf()
    } else if (this.props.dataUrl) {
      this.fetchData(this.props.dataUrl)
    } else {
      this.createPdf(undefined)
    }
    this.triggerGAEvent()
  }

  triggerGAEvent = (): void => {
    const { pathname } = window.location
    let action = ''
    let label = ''

    if (pathname.match(/\/residents\//)) {
      if (pathname.match(/\/charts\//)) {
        action = 'Resident charts'
        // @ts-ignore
        label = pathname.split('/').splice(-1).pop()
      } else if (pathname.match(/\/daily-report$/)) {
        action = 'Resident daily report'
      } else if (pathname.match(/\/profile$/)) {
        action = 'Resident daily report'
      } else if (pathname.match(/\/care-plans$/)) {
        action = 'Hospital passport'
      } else if (pathname.match(/\/care-plans\/$/)) {
        action = 'Resident care plan adl'
        // @ts-ignore
        label = pathname.split('/').splice(-1).pop() // ADL id
      } else if (pathname.match(/\/risk-assessments\/$/)) {
        action = 'Resident risk assessment'
      }
    }

    gaEvent({
      category: 'PDF Download',
      action,
      label
    })
  }

  render(): React.ReactNode {
    const { style, text, disabled } = this.props
    const { isLoading } = this.state
    const displayText = text || BUTTON_TEXT

    return (
      <Button
        dataTestId="pdf-export"
        onClick={this.getPdf}
        style={style}
        variant="default"
        isLoading={isLoading}
        disabled={disabled}
      >
        <FilePdfOutlined />
        {displayText}
      </Button>
    )
  }
}

const BUTTON_TEXT = 'Download PDF'

// `t` is only needed outside render, but PdfExportBase is a class, so the hook
// lives in this wrapper. `useTranslation` (rather than `withTranslation`) keeps
// the component renderable in the many specs that mock react-i18next partially.
const PdfExport = (props: PdfExportProps): React.ReactElement => {
  const { t } = useTranslation(NAMESPACE.GENERAL)

  return <PdfExportBase {...props} t={t} />
}

export default PdfExport
